import {
  TicketCommentResponse,
  ErrorMessageResponse,
  ResponseListType,
  ResponseType,
  SingleTicketResponse,
  TicketFormResponse,
  TicketList,
  MessageTokenResponse,
} from '@/types';
import { useSentryQuery, useSentryMutation, SentryQueryOptions } from '../utils/sentryQueryWrapper';

import {
  createCommentOnTicket,
  createTicket,
  fetchFormFields,
  fetchPendingTicketCount,
  fetchTicketById,
  fetchTicketComments,
  fetchTickets,
  fetchTicketsByIDs,
  filterTickets,
  getMessagingToken,
  updateTicket,
} from '@/api/ticketService';
import { TicketFilters } from '@/stores';
import { TicketSortKeyEnum } from '@/stores/useTicketingStore';

export const useGetMessagingToken = () => {
  return useSentryMutation<ResponseType<MessageTokenResponse>, ErrorMessageResponse, void>(() =>
    getMessagingToken(),
  );
};

// Fetch all Ticket Form Fields
export const useFetchTicketFormFields = (language: 'ar' | 'en', enabled: boolean = true) => {
  return useSentryQuery<ResponseType<TicketFormResponse[]>, Error>(
    ['ticket-fields', language],
    fetchFormFields,
    {
      keepPreviousData: true,
      enabled,
    },
  );
};

export const useFetchTickets = (
  language: 'ar' | 'en',
  page: number,
  pageSize?: number,
  sortBy?: TicketSortKeyEnum,
  enabled?: boolean,
) => {
  return useSentryQuery<ResponseType<TicketList>, Error>(
    ['tickets', language, page, sortBy],
    () => fetchTickets(page, pageSize, sortBy),
    { enabled, cacheTime: 0, staleTime: 0, refetchOnMount: true },
  );
};

export const useFilterTickets = (
  language: 'ar' | 'en',
  page: number,
  pageSize?: number,
  ticketFilters?: TicketFilters,
  platformCode?: string,
  sortBy?: TicketSortKeyEnum,
  customId?: string,
) => {
  return useSentryQuery<ResponseType<TicketList>, Error>(
    [
      'tickets',
      language,
      page,
      ticketFilters ? JSON.stringify(ticketFilters) : null,
      platformCode,
      sortBy,
      customId,
    ],
    () => filterTickets(page, pageSize, ticketFilters, platformCode, sortBy, customId),
    {
      enabled:
        !!platformCode ||
        !!ticketFilters ||
        sortBy !== TicketSortKeyEnum.CREATED_DESC ||
        !!customId,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useFetchTicketsByIDs = (language: 'ar' | 'en', ticketIds?: string[]) => {
  return useSentryQuery<ResponseType<SingleTicketResponse[]>, Error>(
    ['tickets', 'tickets-by-ids', ticketIds, language],
    () => fetchTicketsByIDs(ticketIds ?? []),
    {
      enabled: !!ticketIds && ticketIds.length > 0,
    },
  );
};

export const useFetchTicketById = (
  id?: string,
  language?: 'en' | 'ar',
  options?: SentryQueryOptions<ResponseType<SingleTicketResponse>, Error>,
) => {
  return useSentryQuery<ResponseType<SingleTicketResponse>, Error>(
    ['ticket', id, language],
    () => fetchTicketById(id ?? ''),
    {
      ...options,
      defaultErrorMessage: '',
    },
  );
};

// Update an existing ticket
export const useUpdateTicket = () => {
  return useSentryMutation<any, ErrorMessageResponse, Partial<SingleTicketResponse>>(
    (ticketUpdates: Partial<SingleTicketResponse>) => updateTicket({ ...ticketUpdates }),
  );
};

export const useFetchTicketComments = (id?: string, enabled: boolean = true) => {
  return useSentryQuery<ResponseType<TicketCommentResponse>, Error>(
    ['ticket-comments', id],
    () => fetchTicketComments(id ?? ''),
    {
      enabled: !!id && enabled,
    },
  );
};

// Create a new Ticket
export const useCreateTicket = () => {
  return useSentryMutation<any, ErrorMessageResponse, FormData>((Ticket: FormData) =>
    createTicket(Ticket),
  );
};

// Create a new Comment On Ticket
export const useCreateCommentOnTicket = (ticketId: string) => {
  return useSentryMutation<any, ErrorMessageResponse, FormData>((comment: FormData) =>
    createCommentOnTicket(comment, ticketId),
  );
};

// Fetch ticket counts. The hook surfaces the full /counts response so the
// dashboard Overview card (`total_pending`) and the ticket-center status
// tabs (`by_status`/`summary`) can read the same data without a duplicate
// fetch. Optional date params support the ticket-center page's date filter.
export const useFetchPendingTicketCount = (
  enabled: boolean = true,
  params?: { from_date?: string; to_date?: string },
) => {
  return useSentryQuery<
    {
      status: string;
      object: {
        counts: Record<string, number>;
        by_status: Record<string, number>;
        total: number;
        total_pending: number;
        summary: { active: number; completed: number };
        new?: number;
        open?: number;
      };
    },
    Error
  >(
    ['ticket-pending-count', params?.from_date, params?.to_date],
    () => fetchPendingTicketCount(params),
    { enabled, staleTime: 60000 },
  );
};
